<script lang="ts" setup>
import Vue, { shallowRef } from 'vue';
import { computed } from 'vue';
import { useOrderFormStore } from '@/modules/marketplace/store/order-form-store';
import { store as globalStore } from '@/store/store';
import { JsonObject, Side, create } from '@/connect';
import { OrderIntent } from '@/connect/gen/consts/omsconsts_pb';
import {
  BookOrder,
  BookOrderSchema,
} from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { ApiError } from '@/utils/errors';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { useSocketStore } from '@/store/socket-store';

const props = defineProps<{
  order: BookOrder;
}>();

const socketStore = useSocketStore();
const orderFormStore = useOrderFormStore();

// Note: we leave things in isCanceling state after clicking the cancel button and wait for a socket event to unset that
const isCanceling = shallowRef(false);
socketStore.useEventHandler('refreshVenueOrders', (payload: JsonObject) => {
  if (!isCanceling.value) return;

  const order = create(BookOrderSchema, payload.order as JsonObject);
  if (props.order.clientOrderRef == order.clientOrderRef && !order.active) {
    isCanceling.value = false;
  }
});

const isMyOrder = computed(() => {
  return props.order.companyId === globalStore.state.loginState.user?.companyDisplayId;
});

const isIoi = computed(() => {
  return props.order.orderIntent === OrderIntent.IOI;
});

const matchLabel = computed(() => {
  return isIoi.value ? (props.order.side === Side.BORROW ? 'Offer' : 'Bid') : 'Match';
});

const matchColor = computed(() => {
  return isIoi.value ? '#2196f3' : 'primary';
});

function cancelOrder() {
  const orderRef = props.order.clientOrderRef;
  Vue.prototype.$dialog.ask({
    message: 'You are about to cancel the order.',
    title: 'Order cancellation',
    rejectText: 'Keep order',
    acceptText: 'Cancel order',
    onAccept: async () => {
      isCanceling.value = true;

      try {
        await Vue.prototype.$api.marketplace.cancelOrder(orderRef);
        Vue.prototype.$snackbar.confirm('Order has been successfully canceled');
        // Note: we leave things in isCanceling state and wait for a socket event to unset that
      } catch (err) {
        const errorMessage = new ApiError(i18nServerMessage(err as Error));
        Vue.prototype.$snackbar.error(`Operation failed: ${errorMessage}`);
      }
    },
  });
}
</script>

<template>
  <div class="d-flex wrapper">
    <template v-if="isMyOrder">
      <v-btn
        class="icon-action"
        data-test="edit-order-icon"
        icon
        x-small
        @click="orderFormStore.editOrder(order)"
      >
        <v-icon size="24">mdi-pencil</v-icon>
      </v-btn>
      <v-btn
        class="icon-action"
        data-test="cancel-order-icon"
        :disabled="isCanceling"
        icon
        :loading="isCanceling"
        x-small
        @click="cancelOrder"
      >
        <v-icon size="24">mdi-close-circle</v-icon>
      </v-btn>
    </template>
    <template v-else>
      <v-btn
        :color="matchColor"
        data-test="match-order"
        style="width: 60px"
        x-small
        @click="orderFormStore.matchOrder(order, !isIoi)"
      >
        {{ matchLabel }}
      </v-btn>
    </template>
  </div>
</template>

<style scoped>
.wrapper {
  scale: 0.8;
  padding-top: 3px;
  gap: 0.5rem;
}
</style>
